import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@component/card";
import { Button } from "@component/button";
import {
    Loader2Icon,
    XIcon,
    CheckCircle2Icon,
    AlertCircleIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    FileAudioIcon,
    FileXIcon,
    WifiOffIcon,
} from "lucide-react";
import { cn } from "@util/tailwind";
import { Progress } from "@component/progress";
import { Tooltip } from "@component/tooltip";
import { useAiqmUpload } from "@context/aiqm_upload_context";
import type { AiqmUploadSession } from "@context/aiqm_upload_context";
import { useUserProfileContext } from "@context/user_profile";
import type { CallSessionStatus } from "@type/aiqm";
import { TERMINAL_CALL_STATUSES } from "@type/aiqm";

const SERVER_PHASE_LABEL: Partial<Record<CallSessionStatus, string>> = {
    analyzing:    "Analyzing audio...",
    transcribing: "Transcribing call...",
    scoring:      "Scoring performance...",
};

// Maps the raw upload/processing error string from the AIQM context to a
// plain-language message safe to show in the toast. We intentionally never
// surface axios error text, HTTP status codes, stack traces, or backend
// payload bodies — those are noise to the user and risk leaking internals.
// The raw value is still preserved in the context (and Sentry via the
// surrounding call sites) for debugging.
function toFriendlyUploadError(raw?: string | null): string {
    const text = (raw ?? "").toString().toLowerCase();

    // Network / connectivity
    if (text.includes("network") || text.includes("offline") || text.includes("err_internet")) {
        return "Connection problem. Check your network and try uploading again.";
    }
    // Timeout
    if (text.includes("timeout") || text.includes("etimedout") || text.includes("504") || text.includes("408")) {
        return "The upload took too long. Check your connection and try again.";
    }
    // Canceled / aborted
    if (text.includes("aborted") || text.includes("canceled") || text.includes("cancelled")) {
        return "Upload was canceled. You can try again whenever you're ready.";
    }
    // Authentication
    if (text.includes("401") || text.includes("unauthorized") || text.includes("authentication")) {
        return "Your session has expired. Please sign in again and retry the upload.";
    }
    // Permission
    if (text.includes("403") || text.includes("forbidden") || text.includes("permission")) {
        return "You don't have permission to upload these files.";
    }
    // File too large
    if (text.includes("413") || text.includes("too large") || text.includes("payload")) {
        return "One or more files are too large. Try shorter recordings or compress them first.";
    }
    // Unsupported file type
    if (text.includes("415") || text.includes("unsupported") || text.includes("invalid file") || text.includes("file type")) {
        return "Some files aren't a supported format. Use MP3, WAV, or M4A.";
    }
    // Server-side issues
    if (text.includes("500") || text.includes("502") || text.includes("503") || text.includes("server")) {
        return "Something went wrong on our side. Please try again in a moment.";
    }
    // Default: show the original message verbatim — backend rejections like
    // "Upload limit reached for your plan" are already user-facing, so pass them
    // through rather than flattening to a generic line. Only fall back to the
    // generic copy when there's genuinely nothing to show.
    return (raw ?? "").toString().trim() || "We couldn't finish processing your files. Please try uploading again.";
}

export default function AiqmProcessingToast() {
    const navigate = useNavigate();
    const [, userInfo] = useUserProfileContext();
    const { uploadState, isToastVisible, toastFlashCount, dismissToast, removeSession } = useAiqmUpload();
    const [isExpanded, setIsExpanded] = useState(true);
    const [showBlueBorder, setShowBlueBorder] = useState(false);
    const [isSystemErrorExpanded, setIsSystemErrorExpanded] = useState(true);
    const [isNotCSCallExpanded, setIsNotCSCallExpanded] = useState(true);
    // Surface OS-level connection loss to the toast. The background poller
    // already swallows network errors silently (logs to Sentry), so without
    // this the user only sees an endless "X file remaining" spinner during
    // an outage. We listen to the standard `online`/`offline` events here
    // and render a warning banner inline.
    const [isOffline, setIsOffline] = useState<boolean>(
        typeof navigator !== "undefined" ? !navigator.onLine : false,
    );

    useEffect(() => {
        if (toastFlashCount === 0) return;
        setShowBlueBorder(true);
        const t = window.setTimeout(() => setShowBlueBorder(false), 1500);
        return () => window.clearTimeout(t);
    }, [toastFlashCount]);

    useEffect(() => {
        const onOnline = () => setIsOffline(false);
        const onOffline = () => setIsOffline(true);
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        return () => {
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
        };
    }, []);

    const sessions = uploadState?.sessions ?? [];

    const systemErrorSessions = useMemo(
        () => sessions.filter((s) => s.serverStatus === "failed" && s.errorType === "system_error"),
        [sessions],
    );
    const notCSCallSessions = useMemo(
        () => sessions.filter((s) => s.serverStatus === "failed" && s.errorType === "not_cs_call"),
        [sessions],
    );
    const duplicateSessions = useMemo(
        () => sessions.filter((s) => s.serverStatus === "failed" && s.errorType === "duplicate"),
        [sessions],
    );
    const cancelledSessions = useMemo(
        () => sessions.filter((s) => s.serverStatus === "failed" && s.errorType === "cancelled"),
        [sessions],
    );

    // Only show the toast to an authenticated user. This component is mounted at
    // the app root (outside <Routes>), so without this guard a persisted upload
    // state would render it on public/guest routes like the login page.
    if (!userInfo || !isToastVisible || !uploadState) return null;

    const { phase, pendingFilenames, errorMessage } = uploadState;
    const isTerminal = phase === "done" || phase === "failed";

    const totalCount = sessions.length || pendingFilenames.length;
    const doneCount = sessions.filter((s) => s.serverStatus === "results_ready").length;
    const failedCount = sessions.filter((s) => s.serverStatus === "failed").length;
    const remainingCount = sessions.filter(
        (s) => !TERMINAL_CALL_STATUSES.includes(s.serverStatus),
    ).length;

    // Consolidated batch progress — computed EXACTLY like the bulk-CV toast's
    // `overallProgress`: processing-based, (files done + failed) / total. It is
    // NOT byte-upload progress (bulk-CV has no upload-byte bar), so during the
    // upload phase it sits at "0 of N · 0%" until files start resolving.
    const batchTotal = totalCount;
    const batchProcessed = doneCount + failedCount;
    const batchPct =
        batchTotal > 0 ? Math.min(100, Math.round((batchProcessed / batchTotal) * 100)) : 0;

    // Mirror the bulk-CV toast: for a large batch (>= 20 files) switch to a
    // consolidated view — just the overall progress bar + the failed groups,
    // dropping the long per-file list. Smaller batches keep the per-file rows.
    const showConsolidatedView = totalCount >= 20;

    const headerIcon = (() => {
        if (phase === "done")   return <CheckCircle2Icon className="w-5 h-5 text-emerald-600" />;
        if (phase === "failed") return <AlertCircleIcon  className="w-5 h-5 text-rose-500" />;
        return <Loader2Icon className="w-5 h-5 text-blue-600 animate-spin" />;
    })();

    const headerLabel = (() => {
        if (phase === "uploading") {
            return totalCount > 1
                ? `Uploading ${totalCount} Audio Files`
                : "Uploading Audio File";
        }
        if (phase === "analyzing") return "Analyzing Call Recordings";
        if (phase === "done") {
            return failedCount > 0 ? "Analysis Complete (with errors)" : "Analysis Complete";
        }
        return "Analysis Failed";
    })();

    const subRowText = (() => {
        if (phase === "uploading") {
            return totalCount === 1 ? "1 file uploading..." : `${totalCount} files uploading...`;
        }
        if (phase === "analyzing") {
            // Live per-file truth: succeeded / failed / remaining, each derived
            // from the per-file `status` (not the batch `event`). `doneCount` is
            // the SUCCEEDED count (= the WS `processed`), separate from the
            // handled count (done + failed) that drives the progress bar.
            const parts: string[] = [];
            if (doneCount > 0) parts.push(`${doneCount} succeeded`);
            if (failedCount > 0) parts.push(`${failedCount} failed`);
            parts.push(
                remainingCount === 1 ? "1 file remaining" : `${remainingCount} files remaining`,
            );
            return parts.join(" · ");
        }
        if (phase === "done") {
            // Succeeded count from the per-file statuses (= WS `processed`), shown
            // as "X succeeded of Y" so a batch with failures never reads as all-done.
            if (failedCount > 0) {
                return `${doneCount} succeeded of ${totalCount} · ${failedCount} failed`;
            }
            return totalCount === 1 ? "1 file succeeded" : `${totalCount} files succeeded`;
        }
        return "Processing failed";
    })();

    const nonFailedRows: { filename: string; status: CallSessionStatus | "uploading" }[] =
        sessions.length > 0
            ? sessions
                  .filter((s) => s.serverStatus !== "failed")
                  .map((s) => ({ filename: s.filename, status: s.serverStatus }))
            : pendingFilenames.map((f) => ({
                  filename: f,
                  // On an upload-side failure (markUploadFailed → phase "failed")
                  // the pending files never start processing — render them as
                  // failed instead of a perpetual "uploading" spinner.
                  status: phase === "failed" ? "failed" : "uploading",
              }));

    return (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5 duration-300">
            <Card className={cn(
                "w-[420px] shadow-xl border-border bg-background transition-all duration-300",
                showBlueBorder && "ring-2 ring-blue-500 ring-offset-1",
            )}>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border">
                    <div className="flex items-center gap-2">
                        {headerIcon}
                        <span className="text-sm font-semibold text-foreground">
                            {headerLabel}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-muted"
                            onClick={() => setIsExpanded((e) => !e)}
                        >
                            {isExpanded
                                ? <ChevronDownIcon className="w-4 h-4" />
                                : <ChevronUpIcon className="w-4 h-4" />}
                        </Button>
                        {isTerminal && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-muted"
                                onClick={dismissToast}
                            >
                                <XIcon className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </div>

                {/* Sub-header row — aggregate counts + action buttons */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        {isOffline && !isTerminal && (
                            <WifiOffIcon className="w-3.5 h-3.5 text-amber-600" />
                        )}
                        {isOffline && !isTerminal ? "Waiting for connection..." : subRowText}
                    </span>
                    {phase === "done" && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                            onClick={() => { navigate("/training/aiqm"); dismissToast(); }}
                        >
                            VIEW DETAILS
                        </Button>
                    )}
                    {phase === "failed" && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={dismissToast}
                        >
                            DISMISS
                        </Button>
                    )}
                </div>

                {/* Expanded body */}
                {isExpanded && (
                    <div className="py-1">
                        {/* Consolidated batch progress bar — same as the bulk-CV
                            toast: shown only in the consolidated (>= 20 files)
                            view, with an "X of Y files · %" label driven by the
                            processing count (done + failed), not byte-upload. */}
                        {showConsolidatedView && phase === "analyzing" && (
                            <div className="px-4 py-3 space-y-2 border-b border-border">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">
                                        {batchProcessed} of {batchTotal} files
                                    </span>
                                    <span className="font-medium text-foreground">
                                        {batchPct}%
                                    </span>
                                </div>
                                <Progress
                                    value={batchPct}
                                    className="h-2 [&>div]:bg-blue-600 dark:[&>div]:bg-blue-400"
                                />
                            </div>
                        )}

                        {/* Offline / connection-loss banner */}
                        {isOffline && !isTerminal && (
                            <div className="mx-4 my-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                                <WifiOffIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-amber-800">
                                        You're offline.
                                    </p>
                                    <p className="text-[11px] text-amber-700 mt-0.5">
                                        We'll keep retrying automatically once your connection is back.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Per-file non-failed rows — only in the individual
                            (< 20 files) view; the consolidated view shows just the
                            progress bar above and the failed groups below. */}
                        {!showConsolidatedView && (
                        <div className="divide-y divide-border max-h-48 overflow-y-auto">
                            {/* Non-failed file rows (in-progress + completed) */}
                            {nonFailedRows.map((row, idx) => {
                                const isRowDone = row.status === "results_ready";
                                const isRowFailed = row.status === "failed";
                                const isRowUploading = row.status === "uploading";
                                const isRowProcessing =
                                    !isRowDone && !isRowFailed && !isRowUploading;
                                return (
                                    <div key={idx} className="flex items-center gap-3 px-4 py-2">
                                        <FileAudioIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                        <span
                                            className="text-sm text-foreground truncate flex-1"
                                            title={row.filename}
                                        >
                                            {row.filename}
                                        </span>
                                        {isRowDone && (
                                            <CheckCircle2Icon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                        )}
                                        {isRowFailed && (
                                            <AlertCircleIcon className="w-4 h-4 text-rose-500 flex-shrink-0" />
                                        )}
                                        {(isRowProcessing || isRowUploading) && (
                                            <Loader2Icon className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        )}

                        {/* Failed uploads — grouped by error type */}
                        {(systemErrorSessions.length > 0 || notCSCallSessions.length > 0 || duplicateSessions.length > 0 || cancelledSessions.length > 0) && (
                            <div className="border-t border-border">
                                <div className="px-4 py-2 bg-muted/30">
                                    <span className="text-xs font-medium text-muted-foreground">
                                        Failed Uploads
                                    </span>
                                </div>

                                {/* System Error section */}
                                {systemErrorSessions.length > 0 && (
                                    <div>
                                        <div className="flex items-center justify-between px-4 py-2 bg-background border-y border-border">
                                            <button
                                                onClick={() => setIsSystemErrorExpanded((v) => !v)}
                                                className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                                            >
                                                {isSystemErrorExpanded
                                                    ? <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                                                    : <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />}
                                                <AlertCircleIcon className="w-4 h-4 text-amber-500" />
                                                <span className="text-xs font-medium text-foreground">
                                                    System Error ({systemErrorSessions.length})
                                                </span>
                                            </button>
                                        </div>
                                        {isSystemErrorExpanded && (
                                            <div className="divide-y divide-border max-h-40 overflow-y-auto">
                                                {systemErrorSessions.map((s) => (
                                                    <FailedFileRow
                                                        key={s.sessionId}
                                                        session={s}
                                                        onRemove={() => removeSession(s.sessionId)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Not a CS Call section */}
                                {notCSCallSessions.length > 0 && (
                                    <div>
                                        <div className="flex items-center justify-between px-4 py-2 bg-background border-y border-border">
                                            <button
                                                onClick={() => setIsNotCSCallExpanded((v) => !v)}
                                                className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                                            >
                                                {isNotCSCallExpanded
                                                    ? <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                                                    : <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />}
                                                <FileXIcon className="w-4 h-4 text-destructive" />
                                                <span className="text-xs font-medium text-foreground">
                                                    Not a CS Call ({notCSCallSessions.length})
                                                </span>
                                            </button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                                onClick={() => notCSCallSessions.forEach((s) => removeSession(s.sessionId))}
                                            >
                                                REMOVE ALL
                                            </Button>
                                        </div>
                                        {isNotCSCallExpanded && (
                                            <div className="divide-y divide-border max-h-40 overflow-y-auto">
                                                {notCSCallSessions.map((s) => (
                                                    <FailedFileRow
                                                        key={s.sessionId}
                                                        session={s}
                                                        onRemove={() => removeSession(s.sessionId)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Duplicate section — not retryable, only removable (mirrors bulk-CV) */}
                                {duplicateSessions.length > 0 && (
                                    <FailedErrorSection
                                        icon={<AlertCircleIcon className="w-4 h-4 text-amber-500" />}
                                        label="Duplicate"
                                        sessions={duplicateSessions}
                                        onRemoveAll={() => duplicateSessions.forEach((s) => removeSession(s.sessionId))}
                                        onRemove={removeSession}
                                    />
                                )}

                                {/* Cancelled section — not retryable, only removable (mirrors bulk-CV) */}
                                {cancelledSessions.length > 0 && (
                                    <FailedErrorSection
                                        icon={<XIcon className="w-4 h-4 text-muted-foreground" />}
                                        label="Cancelled"
                                        sessions={cancelledSessions}
                                        onRemoveAll={() => cancelledSessions.forEach((s) => removeSession(s.sessionId))}
                                        onRemove={removeSession}
                                    />
                                )}

                            </div>
                        )}

                        {/* Analyzing sub-label */}
                        {phase === "analyzing" && (
                            <p className="text-xs text-muted-foreground px-4 py-2">
                                {(() => {
                                    const inFlight = sessions.find(
                                        (s) => !TERMINAL_CALL_STATUSES.includes(s.serverStatus),
                                    );
                                    return (inFlight && SERVER_PHASE_LABEL[inFlight.serverStatus]) ?? "Processing...";
                                })()}
                            </p>
                        )}

                        {/* Error message (upload-side failure, no sessions exist) */}
                        {phase === "failed" && sessions.length === 0 && (
                            <div className="mx-4 my-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2">
                                <p className="text-xs text-rose-700">
                                    {toFriendlyUploadError(errorMessage)}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}

interface FailedErrorSectionProps {
    icon: React.ReactNode;
    label: string;
    sessions: AiqmUploadSession[];
    onRemoveAll: () => void;
    onRemove: (sessionId: string) => void;
}

// A non-collapsible failed-uploads group with a REMOVE ALL action and no retry
// (used for Duplicate / Cancelled, which aren't retryable — mirrors the bulk-CV
// toast's ErrorSection).
function FailedErrorSection({ icon, label, sessions, onRemoveAll, onRemove }: FailedErrorSectionProps) {
    if (sessions.length === 0) return null;
    return (
        <div>
            <div className="flex items-center justify-between px-4 py-2 bg-background border-y border-border">
                <div className="flex items-center gap-2">
                    {icon}
                    <span className="text-xs font-medium text-foreground">
                        {label} ({sessions.length})
                    </span>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    onClick={onRemoveAll}
                >
                    REMOVE ALL
                </Button>
            </div>
            <div className="divide-y divide-border max-h-40 overflow-y-auto">
                {sessions.map((s) => (
                    <FailedFileRow key={s.sessionId} session={s} onRemove={() => onRemove(s.sessionId)} />
                ))}
            </div>
        </div>
    );
}

interface FailedFileRowProps {
    session: AiqmUploadSession;
    onRemove: () => void;
}

function FailedFileRow({ session, onRemove }: FailedFileRowProps) {
    // The reason the BE streamed for this failure (the WS `message`), with a
    // sensible per-bucket fallback — surfaced on hover, mirroring the bulk-CV toast.
    const reason =
        session.errorMessage?.trim() ||
        (session.errorType === "duplicate"
            ? "This recording was already uploaded."
            : session.errorType === "cancelled"
                ? "Upload was cancelled."
                : session.errorType === "not_cs_call"
                    ? "This recording isn't a CS call."
                    : "Failed to process this recording.");
    return (
        <div className="flex items-center gap-3 px-4 py-2 hover:bg-muted/30 transition-colors overflow-hidden">
            <FileAudioIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <Tooltip asChild content={<p className="text-xs max-w-[260px] break-words">{reason}</p>}>
                <span className="text-sm text-foreground truncate flex-1 min-w-0 cursor-help">
                    {session.filename}
                </span>
            </Tooltip>
            <button
                onClick={onRemove}
                className="p-1 text-muted-foreground hover:text-destructive rounded hover:bg-muted flex-shrink-0 transition-colors"
                title="Remove"
            >
                <XIcon className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}
